import { deleteTalentMember, getTalentMemberById } from "@fp/admin-api-client/client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import { useGetOrganizationSettings } from "@fp/admin-api-client/organizations/organizations.client";
import { getListPayoutPoliciesQueryKey, useListPayoutPolicies } from "@fp/admin-api-client/payout-policies/payout-policies.client";
import { useGetTalentMemberSignInMethods } from "@fp/admin-api-client/talent-member-sign-in-methods/talent-member-sign-in-methods.client";
import {
  getGetTalentMemberPayoutTermsQueryKey,
  useAssignTalentMemberPayoutPolicy,
  useGetTalentMemberPayoutTerms,
  useSetTalentMemberPushToCard,
} from "@fp/admin-api-client/talent-members/talent-members.client";
import { PaymentDestinationType } from "@fp/admin-api-client/types/paymentDestinationType.js";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { Toggle } from "@fp/ui/components/base/toggle/toggle";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import {
  TalentMemberForm,
  TalentMemberFormActions,
  TalentMemberFormContent,
  TalentMemberFormErrorMessage,
} from "@fp/ui/components/talent-members/talent-member-form";
import { PayoutSetupStatusBadge } from "@fp/ui/components/talent-members/payout-setup-status-badge";
import { IdentityVerificationEntityBadge } from "@fp/ui/components/talent-members/identity-verification-entity-badge";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Trash01 } from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { getErrorMessage } from "@/lib/axios";
import { toast } from "@/lib/toast";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
  type PageAction,
  useSetPageActions,
} from "@/components/page-actions-context";
import { PageActionsMenu } from "@/components/page-actions-menu";
import { PayoutTermsAssignment } from "../-components/payout-terms-assignment";

const PAYOUT_METHOD_LABELS: Record<PaymentDestinationType, string> = {
  [PaymentDestinationType.BankAccount]: "Bank Account",
  [PaymentDestinationType.DebitCard]: "Debit Card",
};

const SSO_PROVIDER_LABELS: Record<string, string> = {
  Apple: "Apple (app)",
  Google: "Google (app)",
  GoogleOpenIdConnect: "Google (web)",
  Microsoft: "Microsoft (web)",
  AzureAD: "Azure Active Directory (web)",
};

export const Route = createFileRoute("/roster/$id/edit")({
  loader: async ({ params }) => {
    const talentMember = await getTalentMemberById(params.id);
    return {
      talentMember: talentMember.data,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { talentMember } = Route.useLoaderData();
  const router = useRouter();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { data: capabilitiesResponse } = useGetOrganizationContextCapabilities();
  const { data: orgSettingsResponse } = useGetOrganizationSettings();
  const { data: signInMethodsResponse } = useGetTalentMemberSignInMethods(talentMember.id);
  const setPushToCardMutation = useSetTalentMemberPushToCard();
  const signInMethods = signInMethodsResponse?.data;
  const queryClient = useQueryClient();

  const canSetPushToCard =
    (capabilitiesResponse?.data?.canSetTalentPushToCard ?? false) &&
    (orgSettingsResponse?.data?.pushToCardEnabled ?? false);

  const showsPayoutTerms = orgSettingsResponse?.data?.payoutPolicyEnabled ?? false;
  const canAssignPayoutPolicy = showsPayoutTerms && (capabilitiesResponse?.data?.canAssignTalentPayoutPolicy ?? false);

  const { data: payoutTermsResponse } = useGetTalentMemberPayoutTerms(talentMember.id, {
    query: { queryKey: getGetTalentMemberPayoutTermsQueryKey(talentMember.id), enabled: showsPayoutTerms },
  });
  const { data: payoutPoliciesResponse } = useListPayoutPolicies({ query: { queryKey: getListPayoutPoliciesQueryKey(), enabled: canAssignPayoutPolicy } });
  const assignPayoutPolicyMutation = useAssignTalentMemberPayoutPolicy();
  const payoutTerms = payoutTermsResponse?.data;
  const payoutPolicies = payoutPoliciesResponse?.data ?? [];

  const handleAssignPayoutPolicy = useCallback(
    async (payoutPolicyId: string) => {
      try {
        const response = await assignPayoutPolicyMutation.mutateAsync({
          id: talentMember.id,
          data: { payoutPolicyId },
        });
        queryClient.setQueryData(getGetTalentMemberPayoutTermsQueryKey(talentMember.id), response);
        toast.success(`This talent member is now on ${response.data.payoutPolicyName ?? "the policy"}'s current terms`);
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to assign the payout policy"));
      }
    },
    [assignPayoutPolicyMutation, talentMember.id, queryClient],
  );

  const handlePushToCardChange = useCallback(
    async (enabled: boolean) => {
      try {
        await setPushToCardMutation.mutateAsync({
          id: talentMember.id,
          data: { enabled },
        });
        await router.invalidate();
        toast.success(
          enabled
            ? "Debit card payouts enabled for this talent member"
            : "Debit card payouts disabled for this talent member",
        );
      } catch (err) {
        toast.error(getErrorMessage(err, "Failed to update debit card payouts"));
      }
    },
    [setPushToCardMutation, talentMember.id, router],
  );

  const handleSaveSuccess = useCallback(async () => {
    await router.invalidate();
    router.navigate({
      to: "/roster",
    });
  }, [router]);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteTalentMember(talentMember.id);
      toast.success("Talent member deleted");
      router.navigate({
        to: "/roster",
      });
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete talent member"));
    } finally {
      setIsDeleting(false);
    }
  }, [talentMember.id, router]);

  useSetPageActions(
    "Talent Member Actions",
    useMemo(() => {
      const actions: PageAction[] = [
        {
          id: "delete",
          icon: Trash01,
          label: "Delete",
          className: "[&_span]:text-error-primary [&_svg]:text-error-primary",
          onAction: () => setShowDeleteModal(true),
        },
      ];
      return actions;
    }, []),
  );

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Edit Talent Member"
        parent={{ url: "/roster", label: "Roster" }}
        titleActions={<PageActionsMenu />}
      />
      <PageContentContainer className="space-y-6 p-0">
        <div className="px-6 pt-6 pb-0">
          <div className="space-y-3 rounded-lg border border-secondary bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">
                Payout Setup
              </span>
              <PayoutSetupStatusBadge status={talentMember.payoutSetupStatus} />
            </div>
            {talentMember.identityVerificationEntityType && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-secondary">
                  Verified As
                </span>
                <IdentityVerificationEntityBadge
                  entityType={talentMember.identityVerificationEntityType}
                  businessName={talentMember.identityVerificationBusinessName}
                />
              </div>
            )}
            <div className="flex items-start justify-between gap-4">
              <span className="text-sm font-medium text-secondary">
                Verified Emails
              </span>
              <span className="text-right text-sm text-tertiary">
                {talentMember.verifiedEmails.length > 0
                  ? talentMember.verifiedEmails.map((verifiedEmail) => (
                      <span key={verifiedEmail.id} className="flex items-center justify-end gap-2">
                        {verifiedEmail.isPrimary && (
                          <Badge size="sm" color="brand">
                            Primary
                          </Badge>
                        )}
                        {verifiedEmail.email}
                      </span>
                    ))
                  : "None yet"}
              </span>
            </div>
            {signInMethods && (
              <>
                <div className="flex items-start justify-between gap-4">
                  <span className="text-sm font-medium text-secondary">
                    Linked Accounts
                  </span>
                  <span className="text-right text-sm text-tertiary">
                    {signInMethods.ssoProviders.length > 0
                      ? signInMethods.ssoProviders.map((ssoProvider) => (
                          <span key={ssoProvider.id} className="flex items-center justify-end gap-2">
                            <Badge size="sm" color="gray">
                              {SSO_PROVIDER_LABELS[ssoProvider.providerId] ?? ssoProvider.providerId}
                            </Badge>
                            {ssoProvider.email}
                          </span>
                        ))
                      : "None"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-secondary">
                    Passkeys
                  </span>
                  <span className="text-sm text-tertiary">
                    {signInMethods.passkeyCount}
                  </span>
                </div>
              </>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">
                Payout Method
              </span>
              <span className="text-sm text-tertiary">
                {talentMember.primaryDestinationType
                  ? PAYOUT_METHOD_LABELS[talentMember.primaryDestinationType]
                  : "Not set"}
              </span>
            </div>
            {canSetPushToCard && (
              <div className="flex items-start justify-between gap-4 border-t border-secondary pt-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-secondary">
                    Debit Card Payouts
                  </span>
                  <span className="text-sm text-tertiary">
                    Let this talent member link a debit card and choose it as their payout method
                  </span>
                </div>
                <Toggle
                  aria-label="Debit card payouts"
                  isSelected={talentMember.pushToCardEnabled}
                  isDisabled={setPushToCardMutation.isPending}
                  onChange={handlePushToCardChange}
                />
              </div>
            )}
            {showsPayoutTerms && payoutTerms && (
              <PayoutTermsAssignment
                payoutTerms={payoutTerms}
                payoutPolicies={payoutPolicies}
                canAssign={canAssignPayoutPolicy}
                isAssigning={assignPayoutPolicyMutation.isPending}
                onAssign={handleAssignPayoutPolicy}
              />
            )}
          </div>
        </div>
        <TalentMemberForm talentMember={talentMember} onSuccess={handleSaveSuccess}>
          <TalentMemberFormContent className="p-6 flex-grow" />
          <TalentMemberFormErrorMessage className="px-6" />
          <TalentMemberFormActions className="bg-white py-6" />
        </TalentMemberForm>
      </PageContentContainer>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Talent Member"
        message="Are you sure you want to delete this talent member? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="primary-destructive"
        isLoading={isDeleting}
      />
    </AdminLayout>
  );
}
